import plur from 'plur'
import {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'
import {shallowEqual} from 'react-redux'

import {usePipelineId} from '../../../../hooks/pipeline'
import {getDraft} from '../../../EditPipelinePage.selectors'
import {collapsedBlocks, pipelineDraft} from '../../../slices/EditPipelinePage.slices'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'
import {getEmptyHash} from 'src/utils/empty'

const COUNTER_LIMIT = 20
const settingsBlockId = 'parameters'

function usePipelineParameters() {
  const [isCreateParameterMode, setCreateParameterMode] = useState<boolean>(false)
  const pipelineId = usePipelineId()
  const parameters: Record<string, string | undefined> = useAppSelector(
    state => getDraft(state, pipelineId)?.settings.parameters ?? getEmptyHash(),
  )
  const dispatch = useAppDispatch()
  const id = useId()
  const keys = useMemo(() => Object.keys(parameters), [parameters])
  const isOutOfLimit = keys.length >= COUNTER_LIMIT
  const count = keys.length
  const prevCount = useRef(count)
  const select = useCallback(
    (key: string) => {
      const input = document.getElementById(`${id}_${key}_name`) as HTMLInputElement | null
      input?.select()
    },
    [id],
  )
  useEffect(() => {
    if (count > prevCount.current) {
      select(keys[count - 1])
    }
    prevCount.current = count
  }, [count, keys, select])
  const addParameter = useCallback(() => {
    setCreateParameterMode(true)
    dispatch(collapsedBlocks.actions.toggle({id: settingsBlockId, collapsed: false}))
  }, [dispatch])
  const deletedParameters = useAppSelector(
    state => state.pipelines.pipelineDraft[pipelineId]?.deleted?.parameters,
    shallowEqual,
  )
  const parametersCount = keys.filter(key => !deletedParameters?.includes(key)).length
  const settingsBlockTitle = parametersCount ? plur('Parameter', parametersCount) : 'No Parameters'
  const isSettingsBlockCollapsible = isCreateParameterMode || count > 0
  const pipelineParameterMultiplyConfirmHandler = useCallback(
    (values: Record<string, string>[]) => {
      values.forEach(({name, value}) =>
        dispatch(
          pipelineDraft.actions.setParameter({
            id: pipelineId,
            name: `${name}`,
            value,
          }),
        ),
      )

      setCreateParameterMode(false)
    },
    [dispatch, pipelineId],
  )

  return {
    settingsBlockId,
    isSettingsBlockCollapsible,
    settingsBlockTitle,
    parameters,
    isOutOfLimit,
    addParameter,
    keys,
    deletedParameters,
    isCreateParameterMode,
    pipelineParameterMultiplyConfirmHandler,
    setCreateParameterMode,
  }
}

export default usePipelineParameters
